<template>
  <MainLayout class="video-lecture-details">
    <div class="container mw-960" slot="action-bar">
      <div class="row">
        <div class="col-md-6 col-lg-7">
          <h5 class="action-header video-course-details__header-title typography--headline typography--color-secondary pt-1 pl-10-mobile pr-10-mobile"
              :title="`${$t('video_lectures.sub_header.video_courses')} > ${course.title} > ${lectureTitle}`"
              v-if="course">
            {{ $t('video_lectures.sub_header.video_courses') }} > {{ course.title }} > {{ lectureTitle }}
          </h5>
          
          <h5 class="action-header video-lecture-details__header-title typography--headline typography--color-secondary pt-1 pl-10-mobile pr-10-mobile"
              :title="`${$t('video_lectures.sub_header.video_lectures')} > ${lectureTitle}`"
              v-else>
            {{ $t('video_lectures.sub_header.video_lectures') }} > {{ lectureTitle }}
          </h5>
        </div>

        <div class="col-md-6 col-lg-5 hidden-sm-down">
          <div class="action-btn-row">
            <router-link class="btn btn-primary float-right ml-1 mr-1" :to="`${$route.path}/edit`">
              {{ $t('video_lectures.video_lecture.edit') }}
            </router-link>
            <button class="btn btn-outline-primary float-right mr-1" @click="$root.$emit('show::modal', 'remove-video-lecture-modal')">
              {{ $t('publishers.headers.actions.remove') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="container mw-620 mt-4 pt-1" slot="layout-body" v-if="loading">
      <div class="row">
        <div class="col-12 text-center">
          <LoadingBounce></LoadingBounce>
          <div class="typography--body-2 typography--color-disabled pb-5">
            {{ $t('common.loading') }}
          </div>
        </div>
      </div>
    </div>

    <div class="container mw-620 mt-4 pt-1" slot="layout-body" v-else>
      <div class="video-lecture-details__video-preview text-center w-100" v-if="videoEmbed">
        <div class="w-100" v-html="videoEmbed"/>
      </div>

      <hr>

      <div class="video-lecture-details__meta" v-if="lecture">
        <div class="typography--title mt-3">
          {{ lectureTitle }}
        </div>

        <div class="typography--body-3 typography--color-secondary mt-2">
          {{ domain && domain.name }} {{ domainConcept && domainConcept.name }}
        </div>

        <div class="typography--body-3 typography--color-secondary mb-3">
          {{ getGradeLabel(lecture) }} {{ getSubjectLabel(lecture) }}
        </div>

        <div class="typography--body-3 typography--color-secondary mb-3">
          <span class="mr-3">
            {{ $t('common.terms.view_count') }} {{ lecture.view_count }}
          </span>
          <span class="mr-3">
            {{ $t('common.terms.like') }} {{ lecture.like_count }}
          </span>
          <span>
            {{ getShareLevelLabel(lecture.share_level || 2) }}
          </span>
        </div>

        <hr>

        <div class="typography--body-1 break-word-all mt-3" v-html="lectureDescription" v-if="lectureDescription"/>

        <div class="typography--body-1 typography--color-disabled mt-3" v-else>
          {{ $t('common.terms.no_description') }}
        </div>

        <div class="typography--body-3 typography--color-disabled mb-5">
          {{ $t('common.terms.posted_date') }}:
          {{ lecture.created_at | datetime }}
        </div>

      </div>
    </div>

    <div slot="modal-area">
      <BasicModal id="remove-video-lecture-modal"
                  :title="$t('video_lectures.modals.remove_lecture.title')"
                  @ok="removeLecture()">
        <div v-html="$t('video_lectures.modals.remove_lecture.message')"></div>
      </BasicModal>
    </div>
  </MainLayout>
</template>

<script>
  import { mapGetters, mapActions } from 'vuex';
  import { isNil, isEmpty } from 'lodash';
  import labels from '@/mixins/labels';
  import publisherOnly from '@/mixins/publisherOnly';

  export default {
    name: 'video-lecture-details',

    mixins: [
      publisherOnly,
      labels,
    ],

    data() {
      return {
        domainConcept: null,
        domain: null,
        loading: false,
      };
    },

    computed: {
      ...mapGetters([
        'videoLectures',
        'videoCourses',
        'subjectMap',
        'subjectIds',
        'gradeMap',
        'gradeIds',
      ]),

      lecture() {
        return this.videoLectures[this.$route.params.lecture_id];
      },

      course() {
        return this.videoCourses[this.$route.params.course_id];
      },

      lectureTitle() {
        if (isNil(this.lecture)) {
          return '';
        }

        return this.lecture.title;
      },

      videoEmbed() {
        if (this.lecture && this.lecture.video && this.lecture.video.embed) {
          return this.lecture.video.embed;
        }

        return null;
      },

      lectureDescription() {
        if (this.lecture && this.lecture.description) {
          return this.lecture.description.replace(/\n/g, '<br>');
        }

        return null;
      },
    },

    methods: {
      ...mapActions([
        'updateVideoLecture',
        'removeVideoLecture',
        'fetchDomainConcept',
        'fetchVideoLecture',
        'fetchVideoCourse',
        'fetchDomain',
      ]),

      removeLecture() {
        this.removeVideoLecture(this.lecture.id)
          .then(() => {
            if (this.course) {
              return this.$router.replace(`/video-courses/${this.course.id}`);
            }

            return this.$router.replace('/video-lectures');
          });
      },
    },

    beforeMount() {
      this.loading = true;

      const lectureId = this.$route.params.lecture_id;
      const courseId = this.$route.params.course_id;

      Promise.resolve()
        .then(() => {
          if (!isNil(courseId) && isEmpty(this.videoCourses[courseId])) {
            return this.fetchVideoCourse(courseId);
          }

          return null;
        })
        .then(() => {
          if (isEmpty(this.videoLectures[lectureId])) {
            return this.fetchVideoLecture(lectureId);
          }

          return this.videoLectures[lectureId];
        })
        .then(() => {
          if (this.lecture && this.lecture.domain_id) {
            return this.fetchDomain(this.lecture.domain_id)
              .then((domain) => {
                this.domain = domain;
              });
          }

          return null;
        })
        .then(() => {
          if (this.lecture && this.lecture.domain_concept_id) {
            return this.fetchDomainConcept(this.lecture.domain_concept_id)
              .then((domainConcept) => {
                this.domainConcept = domainConcept;
              });
          }

          return null;
        })
        .then(() => {
          this.loading = false;
        });
    },
  };
</script>

<style lang="scss">
  .video-lecture-details {
    .video-lecture-details__header-title {
      overflow: hidden;
      text-overflow: ellipsis;
      word-wrap: break-word;
      word-break: break-all;
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 1; /* number of lines to show */
      max-height: 1.5em;    /* fallback */
    }

    .video-lecture-details__video-preview {
      iframe {
        width: 100% !important;
        max-height: 485px !important;
      }
    }
  }
</style>



// WEBPACK FOOTER //
// src/app/video-lectures/views/VideoLectureDetails.vue